/**
 * `<Modal>` — the v3 branded dialog primitive (facelift-v3 F5).
 *
 * The reusable shell behind the `.modal*` design system (Modal.css): blurred
 * backdrop, elevated panel, head (mono eyebrow + lowercase display title +
 * optional sub + close button), scrollable body, and a foot (mono meta on the
 * left, action buttons on the right). Higher-level dialogs compose it:
 * `<PromptModal>` / `<ConfirmModal>` (AppModal.tsx) pass their field/copy as
 * `children` and their buttons as `footActions`.
 *
 * Behavior (matches the retired inline-token idiom so call sites feel the same):
 *   - Esc → onClose (capture phase, beats ancestor key handlers)
 *   - Backdrop click → onClose
 *   - Tab is trapped within the panel; Shift+Tab reverses; both wrap.
 *   - On open, focus moves to `initialFocusRef` if given, else the first
 *     focusable element in the panel.
 *
 * Presentational + behavioral only — it never owns the open/closed state. The
 * caller mounts/unmounts it (so the mount animation fires on each open).
 */

import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import './Modal.css';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  /** Mono caps eyebrow above the title. */
  eyebrow?: string;
  /** Panel heading — rendered in the lowercase display face. */
  title: ReactNode;
  /** Optional supporting paragraph under the title. */
  sub?: string;
  /** Panel max-width. Defaults to `sm` (the prompt/confirm size). */
  size?: 'sm' | 'md' | 'lg' | 'wide';
  /** Esc / backdrop-click / close-button handler. */
  onClose: () => void;
  /** Mono text in the foot's left slot (e.g. a keyboard hint). */
  footMeta?: ReactNode;
  /** Action buttons for the foot's right slot (primary first, row-reverse). */
  footActions?: ReactNode;
  /** Body content. */
  children: ReactNode;
  /** Element to focus on open; falls back to the first focusable in the panel. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Hide the × close button (forced-choice dialogs). */
  hideClose?: boolean;
  /** Accessible label for the dialog when the title isn't a plain string. */
  ariaLabel?: string;
}

export function Modal({
  eyebrow,
  title,
  sub,
  size = 'sm',
  onClose,
  footMeta,
  footActions,
  children,
  initialFocusRef,
  hideClose = false,
  ariaLabel,
}: ModalProps): JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);

  // Auto-focus on open: the caller's preferred element, else the first
  // focusable in the panel. Deferred a tick so the element is mounted.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const target =
        initialFocusRef?.current ??
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
        null;
      target?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [initialFocusRef]);

  // Esc → close. Capture phase so we beat ancestor key handlers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose]);

  // Focus trap — keep Tab within the panel, wrapping at both ends.
  const onPanelKey = (e: React.KeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (panel === null) return;
    const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="modal-backdrop"
      data-open="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
    >
      <div
        ref={panelRef}
        className={`modal modal--${size}`}
        onKeyDown={onPanelKey}
      >
        <div className="modal__head">
          <div className="modal__head-main">
            {eyebrow !== undefined && (
              <div className="modal__eyebrow">{eyebrow}</div>
            )}
            <h2 className="modal__title">{title}</h2>
            {sub !== undefined && <p className="modal__sub">{sub}</p>}
          </div>
          {!hideClose && (
            <button
              type="button"
              className="modal__close"
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <line x1="3" y1="3" x2="11" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <line x1="11" y1="3" x2="3" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        <div className="modal__body">{children}</div>

        {(footActions !== undefined || footMeta !== undefined) && (
          <div className="modal__foot">
            {footMeta !== undefined && (
              <div className="modal__foot-meta">{footMeta}</div>
            )}
            <div className="modal__foot-actions">{footActions}</div>
          </div>
        )}
      </div>
    </div>
  );
}
